// Explore-stats page: Sitting day length (#214).
// Distribution of how long sitting days actually last. Sourced from
// `live_minutes.total_duration_seconds` per (chamber, date). Some
// backfilled docs have null/0 durations (the recorder didn't capture an
// end-time) — those are tallied as "no-duration days" per chamber and
// excluded from the distribution.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack, Alert,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatLine, StatHistogram, PALETTE } from './charts';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Per-chamber grouped histogram. Recharts BarChart with grouped Bars per
// chamber, using the bucket-ordered backend payload.
function ChamberHistogram({ histogram, chambers }) {
  const theme = useTheme();
  const tick = { fontSize: 12, fill: theme.palette.text.secondary };
  // Reshape: { bucket -> { chamber: count } }
  const byBucket = {};
  histogram.forEach((row) => {
    if (!byBucket[row.bucket]) byBucket[row.bucket] = { bucket: row.bucket };
    byBucket[row.bucket][row.chamber] = row.count;
  });
  const BUCKET_ORDER = ['<2h', '2-4h', '4-6h', '6-8h', '8-10h', '10-12h', '>12h'];
  const data = BUCKET_ORDER.map((b) => byBucket[b] || { bucket: b }).filter(Boolean);
  return (
    <ResponsiveContainer width="100%" height={340}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis dataKey="bucket" tick={tick} stroke={theme.palette.text.secondary} />
        <YAxis tick={tick} stroke={theme.palette.text.secondary} />
        <Tooltip contentStyle={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary, fontSize: 12,
        }} />
        <Legend wrapperStyle={{ fontSize: 12,
                                color: theme.palette.text.primary }} />
        {chambers.map((ch, i) => (
          <Bar key={ch} dataKey={ch}
               fill={PALETTE[i % PALETTE.length]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function SittingDayLength() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('sitting-day-length');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const summary = c.by_chamber_summary || [];
  const chambers = summary.map((s) => s.chamber);
  const noDurTotal = summary.reduce((n, s) => n + (s.no_duration_days || 0), 0);

  return (
    <ExploreStatsPage
      title="Sitting day length"
      description="How long sitting days actually last. Sourced from the `live_minutes` index — each doc carries a `total_duration_seconds` for that chamber-day. Distribution histogram, per-chamber comparison, year-over-year trend, day-of-week effect, and shortest / longest single sitting days."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="sitting-day-length"
    >
      {data && (
        <Box>
          {noDurTotal > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {noDurTotal} captured day{noDurTotal === 1 ? '' : 's'} have no
              duration recorded (recorder didn't capture an end-time);
              they're shown in the table but excluded from the distribution.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summary.map((s) => (
              <Grid item xs={12} md={4} key={s.chamber}>
                <StatCard
                  label={`${s.chamber} median`}
                  value={`${Math.round(s.median_min)} min`}
                  sub={`mean ${Math.round(s.mean_min)} min · longest ${Math.round(s.longest_min)} min · n=${s.n}`} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Distribution of sitting-day length (by chamber)
                </Typography>
                <ChamberHistogram histogram={c.histogram || []}
                                  chambers={chambers} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Median day length by day of week (mins)
                </Typography>
                <StatBar data={c.by_dow || []} xKey="dow"
                         yKey="median_min" height={320} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Adjournment-time distribution (hour ended)
                </Typography>
                <StatHistogram data={c.adjournment_hist || []} xKey="hour"
                               yKey="count" height={320} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Median day length by year (mins)
                </Typography>
                <StatLine data={c.per_year || []} xKey="year"
                          keys={['median_min']} height={300} />
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}>
                  Restricted to the captured-live-minutes window (early 2026
                  onwards — Raqeeb backfill + ongoing recorder feed).
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Longest sitting days
                </Typography>
                <Stack spacing={1}>
                  {(c.records?.longest || []).map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2,
                                       borderBottom: '1px solid',
                                       borderColor: 'divider', pb: 1 }}>
                      <Box sx={{ minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
                        {r.date}
                      </Box>
                      <Box sx={{ flex: 1 }}>{r.chamber}</Box>
                      <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                        {r.duration_min} min
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Shortest sitting days
                </Typography>
                <Stack spacing={1}>
                  {(c.records?.shortest || []).map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2,
                                       borderBottom: '1px solid',
                                       borderColor: 'divider', pb: 1 }}>
                      <Box sx={{ minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
                        {r.date}
                      </Box>
                      <Box sx={{ flex: 1 }}>{r.chamber}</Box>
                      <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                        {r.duration_min} min
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
